import { Box, Button, Group, Paper, Stack, Text } from "@mantine/core"
import uniq from "lodash/uniq"
import { useTranslation } from "react-i18next"
import { UserLine } from "@/components/people"
import { AppLoader, DateTime, icons } from "@/components/utils"
import { AdminOnly } from "@/components/utils/permissions"
import { useAPIResources } from "@/hooks"
import { useAPIDownload } from "@/hooks/query/useAPIDownload"
import type { TSessionBase, TUserBase } from "@/types"
import { answersAPI, dayjs, questionsAPI } from "@/utils"
import { MultiAnswer } from "./answers"

export type TSurveyPeerDetailsProps = {
  peer: TUserBase
  session: TSessionBase
}

export function SurveyPeerDetails({ peer, session }: TSurveyPeerDetailsProps) {
  const { t } = useTranslation()
  const { downloadAPIFile, loading } = useAPIDownload()
  const { data: { data: questions = [] } = {}, isPending: isQuestionsPending } = useAPIResources(questionsAPI, {
    where: { session_id: session.id },
  })
  const { data: { data: answers = [] } = {}, isPending: isAnswersPending } = useAPIResources(
    answersAPI,
    {
      where: {
        peer_id: peer.id,
      },
    },
    session.id,
  )
  if (isAnswersPending || isQuestionsPending) return <AppLoader />

  const reviewers = uniq(answers?.map((a) => a.usersession_id)).length

  return (
    <Box maw={800}>
      <Paper mb="sm" p="sm" withBorder>
        <Group justify="space-between" wrap="nowrap">
          <UserLine user={peer} />
          <Stack gap={2} align="flex-end">
            <Text c="dimmed" fz="xl" style={{ whiteSpace: "nowrap" }}>
              <DateTime timestamp={session?.dtstart || 0} />
            </Text>
            <Text c="dimmed" fz="xl" ta="right" fw={500}>
              {t("Reviewers")}: {reviewers}
            </Text>
          </Stack>
        </Group>
      </Paper>
      <Stack>
        {questions.map((question) => (
          <MultiAnswer
            key={question.id}
            question={question}
            answers={answers?.filter((a) => a.question_id === question.id) || []}
          />
        ))}
        <AdminOnly>
          <Group justify="flex-end">
            <Button
              color="gray"
              size="xs"
              loading={loading == "/survey/answers/csv"}
              leftSection={<icons.Download />}
              onClick={() =>
                downloadAPIFile(
                  "/survey/answers/csv",
                  `${session.title ?? "feedback"}-${dayjs(session.dtstart).format("YYYY-MM-DD")}.csv`,
                  {
                    where: {
                      session_id: session.id,
                      peer_id: peer.id,
                    },
                  },
                )
              }
            >
              {t("Download")}
            </Button>
          </Group>
        </AdminOnly>
      </Stack>
    </Box>
  )
}
